import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
  input,
  computed,
  effect,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { Store } from '@ngrx/store';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError, take, withLatestFrom } from 'rxjs/operators';

import {
  FormTemplateService,
  FormResponseService,
  StaffFormFillComponent,
  FormStatus,
  FormFilledByType,
} from '@fitness-force/survey-form-engine';
import type { FormTemplateListItem, FormResponseListItem, FormResponseListResponse } from '@fitness-force/survey-form-engine';
import { ToastMessageService } from '@fitness-force/api';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import { DataGridComponent, NoComponentComponent } from '@fitness-force/grid';
import { DateCellComponent, NewActionsComponent } from '@fitness-force/shared';
import { FormStatusTagComponent } from '../form-status-tag/form-status-tag.component';

@Component({
  selector: 'fitness-force-client-forms-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    DrawerModule,
    RippleModule,
    SelectModule,
    SkeletonModule,
    TooltipModule,
    ConfirmDialogModule,
    StaffFormFillComponent,
    DataGridComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './client-forms-tab.component.html',
  styleUrls: ['./client-forms-tab.component.scss'],
})
export class ClientFormsTabComponent implements OnInit, OnDestroy {
  private readonly templateService = inject(FormTemplateService);
  private readonly responseService = inject(FormResponseService);
  private readonly toastService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly store = inject(Store);

  readonly clientId = input.required<number>();
  readonly tenantId = input.required<number>();

  readonly loading = signal(true);
  readonly templates = signal<FormTemplateListItem[]>([]);
  readonly responses = signal<FormResponseListItem[]>([]);
  readonly columns = signal<any[]>([]);

  protected readonly tableData$ = new BehaviorSubject<FormResponseListItem[]>([]);
  protected readonly columns$ = new BehaviorSubject<any[]>([]);
  protected readonly totalRecordLength$ = new BehaviorSubject({
    totalPage: 1,
    totalItems: 0,
    currentPage: 0,
  });
  protected readonly selectAllData$ = new BehaviorSubject(false);
  protected readonly emptyStateComponent$ = new BehaviorSubject<any>(NoComponentComponent);
  readonly showNewFormDialog = signal(false);
  readonly selectedTemplateId = signal<string | null>(null);
  readonly templateSelectControl = new FormControl<string | null>(null);
  readonly showSendLinkChannelPopup = signal(false);
  readonly selectedSendLinkChannels = signal<Set<string>>(new Set());
  readonly openingFillDrawer = signal(false);
  readonly sendingLink = signal(false);

  readonly drawerVisible = signal(false);
  readonly drawerResponseId = signal<string | null>(null);
  readonly drawerTemplateId = signal<string | null>(null);
  readonly drawerTemplateSchema = signal<unknown>(null);
  readonly drawerResponseData = signal<unknown>(null);
  readonly drawerResponseStatus = signal<FormStatus | string | null>(null);
  readonly drawerReadonly = signal(false);
  /** Set when form submit/complete fails - skip loadData on drawer close */
  private formHadError = false;
  /** Set when component is destroyed - skip loadData on drawer close (tab switch) */
  private isDestroyed = false;

  readonly staffFillableTemplates = computed(() =>
    this.templates().filter((t) => t.allow_staff_fill)
  );

  readonly notificationChannels = [
    { label: 'Email', value: 'EMAIL' },
    { label: 'SMS', value: 'SMS' },
    { label: 'WhatsApp', value: 'WHATSAPP' },
    { label: 'Mobile Notification', value: 'MOBILE_NOTIFICATION' },
  ];

  readonly showFillDrawer = computed(() => this.drawerVisible());

  constructor() {
    this.setupColumns();
    
    // Subscribe to form control changes and update signal
    this.templateSelectControl.valueChanges.subscribe(value => {
      // Convert to string if needed, or null
      this.selectedTemplateId.set(value != null ? String(value) : null);
    });
    
    effect(() => {
      this.tableData$.next(this.responses());
    });
    effect(() => {
      this.columns$.next(this.columns());
    });
    effect(() => {
      const items = this.responses();
      this.totalRecordLength$.next({
        totalPage: 1,
        totalItems: items.length,
        currentPage: 0,
      });
    });
    effect(() => {
      this.selectAllData$.next(false);
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  private setupColumns(): void {
    this.translate
      .get([
        'CLIENT_PROFILE.FORMS.TEMPLATE',
        'CLIENT_PROFILE.FORMS.STATUS',
        'CLIENT_PROFILE.FORMS.COMPLETED_DATE',
        'common.BUTTONS.BTN_ACTIONS',
      ])
      .subscribe((translations) => {
        this.columns.set([
          {
            title: translations['CLIENT_PROFILE.FORMS.TEMPLATE'],
            classes: 'py-2 white-space-nowrap font-bold',
            sortable: true,
            filterable: true,
            dataProperty: 'template_title',
            type: 'default',
          },
          {
            title: translations['CLIENT_PROFILE.FORMS.STATUS'],
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'status',
            type: 'component',
            data: {
              name: FormStatusTagComponent,
              additionData: true,
              aData: { fetch_key: 'status' },
            },
          },
          {
            title: translations['CLIENT_PROFILE.FORMS.COMPLETED_DATE'],
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'completed_date',
            type: 'component',
            data: {
              name: DateCellComponent,
              additionData: true,
              aData: { fetch_key: 'completed_date' },
            },
          },
          {
            title: translations['common.BUTTONS.BTN_ACTIONS'],
            classes: 'py-2 white-space-nowrap',
            sortable: false,
            filterable: false,
            dataProperty: 'actions',
            type: 'component',
            data: {
              name: NewActionsComponent,
              additionData: true,
              aData: ['edit', 'delete', 'copyFormLink'],
            },
          },
        ]);
      });
  }

  loadData(): void {
    const tenantId = this.tenantId();
    const clientId = this.clientId();
    if (!tenantId || !clientId) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    const tenantStr = String(tenantId);
    const clientStr = String(clientId);

    forkJoin({
      templates: this.templateService
        .listTemplates(tenantStr, { is_active: true })
        .pipe(
          take(1),
          catchError((err) => {
            this.toastService.showError(err);
            return of({ items: [] as FormTemplateListItem[] });
          })
        ),
      responses: this.responseService
        .getMemberResponses(tenantStr, clientStr)
        .pipe(
          take(1),
          catchError((err) => {
            this.toastService.showError(err);
            return of({ items: [] as FormResponseListItem[] });
          })
        ),
    })
      .pipe(
        withLatestFrom(
          this.store.select(COMPANY_INFO),
          this.store.select(TENANTS_INFO)
        )
      )
      .subscribe({
      next: ([{ templates, responses }, company, tenants]) => {
        this.templates.set((templates as { items?: FormTemplateListItem[] }).items?.filter((template) => template.is_active === true) ?? []);
        const list = Array.isArray(responses)? responses : ((responses as FormResponseListResponse)?.items ?? []);
        const purchasePortalUrl = this.getPurchasePortalBaseUrl(
          company?.compDetails?.purchase_portal_url ?? ''
        );
        const tenantUuid = tenants?.selected_tenant?.tenant_uuid ?? '';
        this.responses.set(this.enrichResponsesWithMemberFormLink(list, purchasePortalUrl, tenantUuid));
        this.loading.set(false);
      },
      error: (err) => {
        this.toastService.showError(err);
        this.loading.set(false);
      },
    });
  }

  openNewFormDialog(): void {
    this.templateSelectControl.setValue(null, { emitEvent: false });
    this.selectedTemplateId.set(null);
    this.showNewFormDialog.set(true);
  }

  closeNewFormDialog(): void {
    this.showNewFormDialog.set(false);
    this.templateSelectControl.setValue(null, { emitEvent: false });
    this.selectedTemplateId.set(null);
    this.closeSendLinkChannelPopup();
    this.drawerVisible.set(false);
    this.drawerResponseId.set(null);
    this.drawerTemplateId.set(null);
    this.drawerTemplateSchema.set(null);
    this.drawerResponseData.set(null);
    this.drawerResponseStatus.set(null);
  }

  // No longer needed - handled by FormControl valueChanges subscription
  // onTemplateSelect(templateId: string | number | null): void {
  //   this.selectedTemplateId.set(templateId != null ? String(templateId) : null);
  // }

  openSendLinkChannelPopup(): void {
    this.selectedSendLinkChannels.set(new Set());
    this.showSendLinkChannelPopup.set(true);
  }

  closeSendLinkChannelPopup(): void {
    this.showSendLinkChannelPopup.set(false);
    this.selectedSendLinkChannels.set(new Set());
  }

  toggleSendLinkChannel(channel: string): void {
    const current = new Set(this.selectedSendLinkChannels());
    if (current.has(channel)) {
      current.delete(channel);
    } else {
      current.add(channel);
    }
    this.selectedSendLinkChannels.set(current);
  }

  isChannelSelected(channel: string): boolean {
    return this.selectedSendLinkChannels().has(channel);
  }

  getSelectedChannelsCount(): number {
    return this.selectedSendLinkChannels().size;
  }

  getChannelIcon(channel: string): string {
    switch (channel) {
      case 'EMAIL':
        return 'fa fa-envelope';
      case 'SMS':
        return 'fa fa-comments';
      case 'WHATSAPP':
        return 'fab fa-whatsapp';
      case 'MOBILE_NOTIFICATION':
        return 'fa fa-bell';
      default:
        return 'fa fa-paper-plane';
    }
  }

  onFillNow(): void {
    const templateId = this.selectedTemplateId();
    const tenantId = this.tenantId();
    const clientId = this.clientId();
    if (!templateId || !tenantId || !clientId) return;

    this.openingFillDrawer.set(true);
    const tenantStr = String(tenantId);

    this.templateService
      .getTemplate(tenantStr, templateId)
      .pipe(take(1))
      .subscribe({
        next: (template) => {
          const schema =
            template.schema_json ??
            (template as { clt_frm_tmplt_schema_json?: object }).clt_frm_tmplt_schema_json;
          if (!schema || typeof schema !== 'object') {
            this.toastService.showErrorMessage('Form template has no schema');
            this.openingFillDrawer.set(false);
            return;
          }
          this.drawerResponseId.set(null);
          this.drawerTemplateId.set(templateId);
          this.drawerTemplateSchema.set(schema);
          this.drawerResponseData.set(null);
          this.drawerResponseStatus.set(null);
          this.drawerVisible.set(true);
          this.openingFillDrawer.set(false);
        },
        error: (err) => {
          this.toastService.showError(err);
          this.openingFillDrawer.set(false);
        },
      });
  }

  onSendLink(): void {
    const templateId = this.selectedTemplateId();
    const channels = Array.from(this.selectedSendLinkChannels());
    const tenantId = this.tenantId();
    const clientId = this.clientId();
    if (!templateId || channels.length === 0 || !tenantId || !clientId) return;

    this.sendingLink.set(true);
    const tenantStr = String(tenantId);
    const clientStr = String(clientId);

    this.responseService
      .createResponse(tenantStr, {
        template_id: parseInt(templateId, 10),
        client_id: clientStr,
        status: FormStatus.PENDING,
        response_json: {},
        notification_channel: channels,
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.sendingLink.set(false);
          this.toastService.showSuccess('Form link sent successfully');
          this.closeSendLinkChannelPopup();
          this.closeNewFormDialog();
          this.loadData(); // Reload data to show the new response
        },
        error: (err) => {
          this.sendingLink.set(false);
          this.toastService.showError(err);
        },
      });
  }

  closeFillDrawer(): void {
    this.drawerVisible.set(false);
    this.drawerResponseId.set(null);
    this.drawerTemplateId.set(null);
    this.drawerTemplateSchema.set(null);
    this.drawerResponseData.set(null);
    this.drawerResponseStatus.set(null);
    this.drawerReadonly.set(false);
    if (!this.isDestroyed && !this.formHadError) {
      this.loadData();
    }
    this.formHadError = false;
  }

  onFormCompleted(): void {
    if (this.drawerResponseId()) {
      this.closeFillDrawer();
    } else {
      this.loadData();
      this.closeNewFormDialog();
    }
  }

  onFormError(): void {
    this.formHadError = true;
  }

  handleAction(event: { action: string; columnData: FormResponseListItem }): void {
    switch (event.action) {
      case 'edit':
        this.onEditResponse(event.columnData);
        break;
      case 'delete':
        this.confirmDeleteResponse(event.columnData);
        break;
      case 'copyFormLink':
        this.copyMemberFormLink(event.columnData);
        break;
    }
  }

  private enrichResponsesWithMemberFormLink(
    items: FormResponseListItem[],
    purchasePortalUrl: string,
    tenantUuid: string
  ): FormResponseListItem[] {
    return items.map((item) => ({
      ...item,
      member_form_link: this.buildMemberFormLink(item, purchasePortalUrl, tenantUuid),
    }));
  }

  private buildMemberFormLink(
    item: FormResponseListItem,
    purchasePortalUrl: string,
    tenantUuid: string
  ): string | null {
    const template = this.templates().find((t) => t.id === item.form_template_id);
    if (
      !template || template?.allow_member_fill === false ||
      !item.uuid ||
      !purchasePortalUrl ||
      !tenantUuid
    ) {
      return null;
    }
    return `${purchasePortalUrl}/tenants/${tenantUuid}/forms/${item.uuid}`;
  }

  private getPurchasePortalBaseUrl(url: string): string {
    const trimmed = url?.trim();
    if (!trimmed) {
      return '';
    }
    let normalized = trimmed;
    if (!/^https?:\/\//i.test(normalized)) {
      normalized = `https://${normalized}`;
    }
    try {
      const parsedUrl = new URL(normalized);
      return `${parsedUrl.protocol}//${parsedUrl.hostname}`;
    } catch {
      return '';
    }
  }

  private copyMemberFormLink(response: FormResponseListItem): void {
    const link = response.member_form_link;
    if (!link) {
      return;
    }

    if (navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => {
        this.translate.get('common.message.COPY_SUCCESS').pipe(take(1)).subscribe((message: string) => {
          this.toastService.showSuccess(message);
        });
      }).catch(() => {
        this.fallbackCopyToClipboard(link);
      });
      return;
    }

    this.fallbackCopyToClipboard(link);
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.translate.get('common.message.COPY_SUCCESS').pipe(take(1)).subscribe((message: string) => {
          this.toastService.showSuccess(message);
        });
      } else {
        throw new Error('Copy command failed');
      }
    } catch {
      this.toastService.showError('Failed to copy to clipboard');
    } finally {
      if (textArea.parentNode === document.body) {
        textArea.remove();
      }
    }
  }

  onRowSelect(event: { data: FormResponseListItem; selection: boolean }): void {
    if (event.selection && event.data) {
      this.onEditResponse(event.data);
    }
  }

  onEditResponse(response: FormResponseListItem): void {
    const tenantId = this.tenantId();
    const responseId = response.id?.toString();
    if (!tenantId || !responseId) return;

    this.openingFillDrawer.set(true);
    const tenantStr = String(tenantId);

    this.responseService
      .getFormResponseContext(tenantStr, responseId)
      .pipe(take(1))
      .subscribe({
        next: (context) => {
          const schema = context.template_schema;
          if (!schema || typeof schema !== 'object') {
            this.toastService.showErrorMessage('Form template has no schema');
            this.openingFillDrawer.set(false);
            return;
          }

          let responseData = null;
          try {
            if (context.current_data) {
              responseData = typeof context.current_data === 'string' 
                ? JSON.parse(context.current_data) 
                : context.current_data;
            }
          } catch (error) {
            responseData = null;
          }

          this.drawerResponseId.set(responseId);
          this.drawerTemplateId.set(null);
          this.drawerTemplateSchema.set(schema);
          this.drawerResponseData.set(responseData);
          this.drawerResponseStatus.set(response.status);
          //this.drawerReadonly.set(true);
          this.drawerVisible.set(true);
          this.openingFillDrawer.set(false);
        },
        error: (err) => {
          this.toastService.showError(err);
          this.openingFillDrawer.set(false);
        },
      });
  }

  confirmDeleteResponse(response: FormResponseListItem): void {
    this.translate
      .get([
        'CLIENT_PROFILE.FORMS.DELETE_CONFIRMATION_MESSAGE',
        'CLIENT_PROFILE.FORMS.DELETE_CONFIRMATION_HEADER',
        'common.BUTTONS.BUTTON-TEXT-YES',
        'common.BUTTONS.BUTTON-TEXT-NO',
      ])
      .subscribe((translations) => {
        this.confirmationService.confirm({
          message: translations['CLIENT_PROFILE.FORMS.DELETE_CONFIRMATION_MESSAGE'],
          header: translations['CLIENT_PROFILE.FORMS.DELETE_CONFIRMATION_HEADER'],
          acceptLabel: translations['common.BUTTONS.BUTTON-TEXT-YES'],
          rejectLabel: translations['common.BUTTONS.BUTTON-TEXT-NO'],
          icon: 'pi pi-exclamation-triangle',
          acceptButtonStyleClass: 'p-button-danger',
          accept: () => {
            this.deleteResponse(response);
          },
        });
      });
  }

  private deleteResponse(response: FormResponseListItem): void {
    const tenantId = this.tenantId();
    const responseId = response.id?.toString();
    if (!tenantId || !responseId) return;

    const tenantStr = String(tenantId);
    this.responseService
      .deleteResponse(tenantStr, responseId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastService.showSuccess('Form deleted successfully');
          this.loadData();
        },
        error: (err) => {
          this.toastService.showError(err);
        },
      });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.closeFillDrawer();
    this.closeNewFormDialog();
    this.closeSendLinkChannelPopup();
    this.templateSelectControl.setValue(null, { emitEvent: false });
    this.selectedTemplateId.set(null);
    this.selectedSendLinkChannels.set(new Set());
    this.openingFillDrawer.set(false);
    this.sendingLink.set(false);
    this.drawerVisible.set(false);
    this.drawerResponseId.set(null);
    this.drawerTemplateId.set(null);
    this.drawerTemplateSchema.set(null);
    this.drawerResponseData.set(null);
    this.drawerResponseStatus.set(null);
    this.drawerReadonly.set(false);
  }
}
